import { useState, useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { pb, InspirePost } from '@/lib/pocketbase';
import { linkifyWithBreaks } from '@/utils/linkify';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Heart, MessageCircle, Share2, Edit, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';
import { EditInspirePostModal } from '@/components/EditInspirePostModal';
import { PostDetailModal } from '@/components/PostDetailModal';
import { detectTextDirection } from '@/utils/textDirection';
import { Separator } from '@/components/ui/separator';
import { useNavigate } from 'react-router-dom';
import { ImageCarousel } from '@/components/inspire/ImageCarousel';

interface TutorPostsTabProps {
  tutorId: string;
}

export const TutorPostsTab = ({ tutorId }: TutorPostsTabProps) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [posts, setPosts] = useState<InspirePost[]>([]);
  const [likes, setLikes] = useState<{ [postId: string]: boolean }>({});
  const [isLoading, setIsLoading] = useState(true);
  const [editingPost, setEditingPost] = useState<{ id: string; content: string; image?: string } | null>(null);
  const [selectedPost, setSelectedPost] = useState<InspirePost | null>(null);
  const [postToDelete, setPostToDelete] = useState<string | null>(null);
  const [expandedPosts, setExpandedPosts] = useState<Set<string>>(new Set());
  const [showAuthModal, setShowAuthModal] = useState(false);

  // Fetch posts for this tutor
  const fetchPosts = async () => {
    try {
      const records = await pb.collection('inspire_posts').getFullList({
        filter: `tutor_id="${tutorId}"`,
        sort: '-display_order,-created',
        expand: 'tutor_id,tutor_id.user'
      });
      
      // Calculate actual like counts for each post
      const postsWithCounts = await Promise.all(
        records.map(async (post) => {
          try {
            const likesResult = await pb.collection('inspire_likes').getList(1, 1, {
              filter: `post_id="${post.id}"`,
              $count: true
            });
            
            const commentsResult = await pb.collection('inspire_comments').getList(1, 1, {
              filter: `post_id="${post.id}"`,
              $count: true
            });
            
            const sharesResult = await pb.collection('inspire_post_shares').getList(1, 1, {
              filter: `post_id="${post.id}"`,
              $count: true
            });
            
            return {
              ...post,
              likes_count: likesResult.totalItems || 0,
              comments_count: commentsResult.totalItems || 0,
              shares_count: sharesResult.totalItems || 0
            };
          } catch (error) {
            console.error('Error counting for post:', post.id, error);
            return post;
          }
        })
      );
      
      setPosts(postsWithCounts as InspirePost[]);
      
      // Check user likes for each post
      if (user) {
        const userLikes: { [postId: string]: boolean } = {};
        for (const post of records) {
          try {
            await pb.collection('inspire_likes').getFirstListItem(
              `post_id="${post.id}" && user_id="${user.id}"`
            );
            userLikes[post.id] = true;
          } catch {
            userLikes[post.id] = false;
          }
        }
        setLikes(userLikes);
      }
    } catch (error) {
      console.error('Error fetching posts:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchPosts();

    // Set up real-time subscriptions
    const unsubscribePosts = pb.collection('inspire_posts').subscribe('*', (e) => {
      fetchPosts();
    });

    const unsubscribeComments = pb.collection('inspire_comments').subscribe('*', () => {
      fetchPosts();
    });

    const unsubscribeLikes = pb.collection('inspire_likes').subscribe('*', () => {
      fetchPosts();
    });

    const unsubscribeShares = pb.collection('inspire_post_shares').subscribe('*', () => {
      fetchPosts();
    });

    return () => {
      Promise.all([unsubscribePosts, unsubscribeComments, unsubscribeLikes, unsubscribeShares])
        .then(unsubscribers => unsubscribers.forEach(unsub => unsub()))
        .catch(console.error);
    };
  }, [user, tutorId]);

  const handleLike = async (postId: string, fromModal: boolean = false) => {
    if (!user) {
      const post = posts.find(p => p.id === postId);
      if (post && fromModal) {
        setSelectedPost(post);
      }
      setShowAuthModal(true);
      return;
    }

    try {
      const isLiked = likes[postId];
      
      if (isLiked) {
        const existingLike = await pb.collection('inspire_likes').getFirstListItem(
          `post_id="${postId}" && user_id="${user.id}"`
        );
        await pb.collection('inspire_likes').delete(existingLike.id);
        setLikes(prev => ({ ...prev, [postId]: false }));
      } else {
        await pb.collection('inspire_likes').create({
          post_id: postId,
          user_id: user.id
        });
        
        // Send notification to post author
        const post = posts.find(p => p.id === postId);
        const postAuthorId = post?.expand?.tutor_id?.user;
        
        if (postAuthorId && postAuthorId !== user.id) {
          try {
            await pb.collection('notifications').create({
              userId: postAuthorId,
              type: 'comment_reaction',
              title: 'New like on your post',
              message: `${user.name || 'Someone'} liked your post`,
              isRead: false,
              createdAt: new Date().toISOString(),
              data: {
                postId: postId
              }
            });
          } catch (error) {
            console.error('Error creating like notification:', error);
          }
        }
        
        setLikes(prev => ({ ...prev, [postId]: true }));
      }
    } catch (error) {
      console.error('Error handling like:', error);
      toast.error('Failed to update like');
    }
  };

  const handleShare = async (postId: string) => {
    try {
      const shareUrl = `https://tutornesta.com/inspire-posts?post=${postId}`;
      await navigator.clipboard.writeText(shareUrl);
      toast.success('Post link copied to clipboard!');
      
      await pb.collection('inspire_post_shares').create({
        post_id: postId,
        user_id: user?.id || null
      });
    } catch (error) {
      console.error('Error sharing post:', error);
      toast.error('Failed to share post');
    }
  };

  const handleDeletePost = async () => {
    if (!user || !postToDelete) return;
    
    try {
      await pb.collection('inspire_posts').delete(postToDelete);
      setPosts(prev => prev.filter(post => post.id !== postToDelete));
      toast.success('Post deleted successfully');
      setPostToDelete(null);
    } catch (error) {
      console.error('Error deleting post:', error);
      toast.error('Failed to delete post');
    }
  };

  const handleEditPost = (post: InspirePost) => {
    setEditingPost({ 
      id: post.id, 
      content: post.content,
      image: post.image 
    });
  };

  const canModifyPost = (post: InspirePost) => {
    if (!user) return false;
    return user.role === 'admin' || post.expand?.tutor_id?.user === user.id;
  };

  const getImageUrl = (post: InspirePost) => {
    if (!post.image) return null;
    return `${pb.baseUrl}/api/files/inspire_posts/${post.id}/${post.image}`;
  };

  const getImageUrls = (post: InspirePost): string[] => {
    const urls: string[] = [];
    // Multi-image field (array)
    if (post.images && Array.isArray(post.images) && post.images.length > 0) {
      post.images.forEach((img: string) => {
        urls.push(`${pb.baseUrl}/api/files/inspire_posts/${post.id}/${img}`);
      });
    } else if (post.image) {
      // Fallback to legacy single image
      urls.push(`${pb.baseUrl}/api/files/inspire_posts/${post.id}/${post.image}`);
    }
    console.log('TutorPostsTab getImageUrls for post', post.id, ':', { images: post.images, image: post.image, resultUrls: urls });
    return urls;
  };

  const getVideoUrl = (post: InspirePost) => {
    if (!post.video) return null;
    return `${pb.baseUrl}/api/files/inspire_posts/${post.id}/${post.video}`;
  };

  const getTutorInfo = (post: InspirePost) => {
    const tutor = post.expand?.tutor_id;
    const tutorUser = tutor?.expand?.user;
    
    let avatar = '';
    if (tutor?.profilePictureUrl) {
      avatar = tutor.profilePictureUrl;
    } else if (tutor?.profilePicture) {
      avatar = pb.files.getURL(tutor, tutor.profilePicture);
    } else if (tutorUser?.avatar) {
      avatar = tutorUser.avatar;
    }
    
    return {
      name: tutor?.name || tutorUser?.name || 'Unknown Tutor',
      avatar
    };
  };

  const toggleExpandPost = (postId: string) => {
    setExpandedPosts(prev => {
      const newSet = new Set(prev);
      if (newSet.has(postId)) {
        newSet.delete(postId);
      } else {
        newSet.add(postId);
      }
      return newSet;
    });
  };

  const shouldTruncate = (content: string) => {
    const lines = content.split('\n');
    return lines.length > 3 || content.length > 150;
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (posts.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground text-sm">
        No posts yet
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {posts.map((post, index) => {
        const tutorInfo = getTutorInfo(post);
        const imageUrl = getImageUrl(post);
        const videoUrl = getVideoUrl(post);
        const isLiked = likes[post.id] || false;
        const textDir = detectTextDirection(post.content);
        const isExpanded = expandedPosts.has(post.id);
        const needsTruncation = shouldTruncate(post.content);

        return (
          <div key={post.id}>
            <Card className="w-full">
              <CardHeader className="pb-2 pt-3 px-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Avatar className="h-8 w-8">
                      <AvatarImage src={tutorInfo.avatar} />
                      <AvatarFallback className="text-xs">{tutorInfo.name.charAt(0)}</AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="text-sm font-semibold">{tutorInfo.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {formatDistanceToNow(new Date(post.created), { addSuffix: true })}
                      </p>
                    </div>
                  </div>
                  
                  {canModifyPost(post) && (
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEditPost(post)}
                        className="h-7 w-7 p-0"
                      >
                        <Edit className="h-3 w-3" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setPostToDelete(post.id)}
                        className="h-7 w-7 p-0 text-destructive hover:text-destructive"
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  )}
                </div>
              </CardHeader>
              
              <CardContent className="px-3 pb-3 space-y-2">
                <div
                  className={`text-sm whitespace-pre-wrap break-words ${!isExpanded && needsTruncation ? 'line-clamp-3' : ''}`}
                  dir={textDir}
                  dangerouslySetInnerHTML={{ __html: linkifyWithBreaks(post.content) }}
                />
                
                {needsTruncation && (
                  <button
                    onClick={() => toggleExpandPost(post.id)}
                    className="text-xs text-primary hover:underline"
                  >
                    {isExpanded ? 'Show less' : 'Show more'}
                  </button>
                )}
                
                {videoUrl ? (
                  <video 
                    src={videoUrl}
                    className="w-full rounded-md max-h-80 object-contain"
                    controls
                    playsInline
                    autoPlay
                    muted
                    loop
                    preload="metadata"
                  />
                ) : getImageUrls(post).length > 1 ? (
                  <ImageCarousel
                    images={getImageUrls(post)}
                    className="h-[400px] rounded-md overflow-hidden"
                    showArrows={true}
                    showDots={true}
                    onImageClick={() => setSelectedPost(post)}
                  />
                ) : imageUrl ? (
                  <img 
                    src={imageUrl} 
                    alt="Post image" 
                    className="w-full rounded-md object-contain max-h-[500px] md:max-h-none md:object-cover cursor-pointer"
                    onClick={() => setSelectedPost(post)}
                  />
                ) : null}
                
                <div className="flex items-center gap-4 pt-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleLike(post.id)}
                    className={`h-8 px-2 ${isLiked ? 'text-red-500' : ''}`}
                  >
                    <Heart className={`h-4 w-4 mr-1 ${isLiked ? 'fill-current' : ''}`} />
                    <span className="text-xs">{post.likes_count || 0}</span>
                  </Button>
                  
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setSelectedPost(post)}
                    className="h-8 px-2"
                  >
                    <MessageCircle className="h-4 w-4 mr-1" />
                    <span className="text-xs">{post.comments_count || 0}</span>
                  </Button>
                  
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleShare(post.id)}
                    className="h-8 px-2"
                  >
                    <Share2 className="h-4 w-4 mr-1" />
                    <span className="text-xs">{post.shares_count || 0}</span>
                  </Button>
                </div>
              </CardContent>
            </Card>

            {index < posts.length - 1 && <Separator className="my-3" />}
          </div>
        );
      })}

      {editingPost && (
        <EditInspirePostModal
          isOpen={!!editingPost}
          onClose={() => setEditingPost(null)}
          postId={editingPost.id}
          initialContent={editingPost.content}
          initialImage={editingPost.image}
          onSuccess={() => {
            setEditingPost(null);
            fetchPosts();
          }}
        />
      )}

      {selectedPost && (
        <PostDetailModal
          isOpen={!!selectedPost}
          onClose={() => setSelectedPost(null)}
          post={selectedPost}
          isLiked={likes[selectedPost.id] || false}
          onLike={() => handleLike(selectedPost.id, true)}
          onShare={() => handleShare(selectedPost.id)}
          userId={user?.id}
          onAuthRequired={() => {
            navigate(`/auth?mode=login&redirect=/tutor/${tutorId}`);
          }}
        />
      )}

      <Dialog open={showAuthModal} onOpenChange={setShowAuthModal}>
        <DialogContent className="max-w-md animate-in fade-in-0 zoom-in-95 duration-300">
          <DialogHeader>
            <DialogTitle>Join Our Community</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-muted-foreground text-sm">
              Sign in to interact with posts, like content, and join the conversation with our amazing tutors.
            </p>
            <div className="space-y-3">
              <Button 
                onClick={() => {
                  setShowAuthModal(false);
                  const redirectUrl = selectedPost 
                    ? `/auth?mode=login&redirect=/tutor/${tutorId}`
                    : `/auth?mode=login&redirect=/tutor/${tutorId}`;
                  navigate(redirectUrl);
                }}
                className="w-full"
              >
                Sign In
              </Button>
              <Button 
                variant="outline"
                onClick={() => {
                  setShowAuthModal(false);
                  const redirectUrl = selectedPost 
                    ? `/auth?mode=register&redirect=/tutor/${tutorId}`
                    : `/auth?mode=register&redirect=/tutor/${tutorId}`;
                  navigate(redirectUrl);
                }}
                className="w-full"
              >
                Create Account
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!postToDelete} onOpenChange={() => setPostToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Post</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this post? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeletePost}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
